"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const PupsExceptions_1 = require("./PupsExceptions");
const PupsConfig_1 = require("./PupsConfig");
/**
 * Classe principale
 *
 * @author Gennaro Bosone <gennaro.bs@gmail.com>
 * @class Pups
 */
class Pups {
    /**
     * Creazione dell'istanza (unica) di Pups
     * @param {Types.PupsConfig.File} pupsConfig
     * @memberof Pups
     */
    constructor(pupsConfig) {
        /**
         * Lista dei componenti creati
         *
         * @private
         * @type {Object}
         * @memberof Pups
         */
        this.components = {};
        this.pupsConfig = new PupsConfig_1.default(pupsConfig);
        // Set alias for colors of palette.
        // Example: pups.color('primary') => pups.primary
        this.palette.getColors().map((value) => {
            const c = value;
            Object.defineProperty(this, c.getCcName(), {
                enumerable: false, configurable: false, writable: false, value: this.color(c.getCcName())
            });
        });
    }
    getPupsConfig() {
        return this.pupsConfig;
    }
    //#region Palette
    get palette() {
        return this.pupsConfig.getPalette();
    }
    color(name, format) {
        return this.palette.get(name, format || this.palette.getFormat());
    }
    //#endregion
    //#region ModularScale
    get modularScale() {
        return this.pupsConfig.getModularScale();
    }
    /**
     * Ritorna il valore scalato di 'increment' unità
     *
     * @param {number} increment L'incremento può essere sia positivo che negativo
     * @returns {string}
     * @memberof Pups
     */
    ms(increment, toInt = false) {
        const i = this.modularScale.get(increment);
        return toInt ? parseInt(i) : i;
    }
    /**
     * Esegue un'addizione su diversi tipi di misure
     * Ogni numero verrà converito nel modular scale
     * Esempio: pups.add(1, '2rem', '4px')
     *
     * @param {Array<string | number>} args Valori da addizionare
     * @returns {string}
     * @memberof Pups
     */
    add(...args) {
        return this.modularScale.add.apply(this.modularScale, args);
    }
    /**
     * Esegue una sottrazione su diversi tipi di misure
     * Ogni numero verrà converito nel modular scale
     * Esempio: pups.sub(1, '2rem', '4px')
     *
     * @param {Array<string | number>} args Valori da addizionare
     * @returns {string}
     * @memberof Pups
     */
    sub(...args) {
        return this.modularScale.sub.apply(this.modularScale, args);
    }
    /**
     * Esegue una moltiplocazione su diversi tipi di misure
     * Ogni numero verrà converito nel modular scale
     * Esempio: pups.mul(1, '2rem', '4px')
     *
     * @param {Array<string | number>} args Valori da addizionare
     * @returns {string}
     * @memberof Pups
     */
    mul(...args) {
        return this.modularScale.mul.apply(this.modularScale, args);
    }
    /**
     * Esegue una divisione su diversi tipi di misure
     * Ogni numero verrà converito nel modular scale
     * Esempio: pups.div(1, '2rem', '4px')
     *
     * @param {Array<string | number>} args Valori da addizionare
     * @returns {string}
     * @memberof Pups
     */
    div(...args) {
        return this.modularScale.div.apply(this.modularScale, args);
    }
    /**
     * Ritorna il ratio per lo scale modulare
     *
     * @readonly
     * @type {number}
     * @memberof Pups
     */
    get ratio() {
        return this.modularScale.getRatio();
    }
    //#endregion
    //#region Typography
    get typography() {
        return this.pupsConfig.getTypography();
    }
    //#endregion
    //#region MediaQuery
    get mediaQuery() {
        return this.pupsConfig.getMediaQuery();
    }
    /**
     * Ritorna la media query
     *
     * @param {string} slug
     * @param {string} css
     * @returns {string} css
     * @memberof Pups
     */
    mq(slug, css = null) {
        return this.mediaQuery.get(slug, css);
    }
    //#endregion
    //#region Animation
    get animation() {
        return this.pupsConfig.getAnimation();
    }
    tm(name, withoutUnit) {
        return this.animation.getTiming(name, withoutUnit);
    }
    es(name) {
        return this.animation.getEasing(name);
    }
    //#endregion
    //#region Components
    /**
     * Crea un nuovo componente miscelando più componenti esistenti o nuovi
     *
     * @param {any} arguments L'argomento 0 deve essere una stringa (il nome del nuovo componente).
     *                        Gli altri argomenti possono essere di tipo string (nome dei componenti esistenti)
     *                        oppure nuove proprietà (object).
     * @memberof Pups
     */
    define() {
        const args = Array.from(arguments);
        if (typeof args[0] !== 'string')
            throw new PupsExceptions_1.DefineComponentException();
        else if (args[0] in this.components)
            console.warn(`Il componente ${args[0]} è già esistente e verrà sostituito.`);
        this.setComponent(args.shift(), Object.assign.apply(null, args.map(p => typeof p === 'string' ? this.getComponent(p) : p)));
    }
    /**
     * Ritorna le proprietà di un componente
     *
     * @param {string} name
     * @returns {Object}
     * @memberof Pups
     */
    require(name) {
        return this.getComponent(name);
    }
    /**
     * Ritorna le proprietà di un componente
     *
     * @param {string} name
     * @returns {Object}
     * @memberof Pups
     */
    getComponent(name) {
        if (name in this.components)
            return this.components[name];
        throw new PupsExceptions_1.ComponentNotFoundException(name);
    }
    /**
     *  Setta o aggiorna le proprietà di un componente
     *
     * @private
     * @param {string} name
     * @param {Object} properties
     * @memberof Pups
     */
    setComponent(name, properties) {
        if (name in this.components)
            console.warn(`Il componente ${name} è già esistente e verrà sostituito.`);
        this.components[name] = properties;
    }
}
exports.default = Pups;
//# sourceMappingURL=Pups.js.map